import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  CheckCircle2,
  PlayCircle,
  Plus,
  RefreshCcw,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  createDigest,
  deleteDigest,
  listDigests,
  listRecentDigestRuns,
  listSavedQuestions,
  runDigestNow,
  type DigestRun,
  type DigestSchedule,
  type SavedQuestion,
} from "@/lib/api";

/**
 * Cron-driven scheduled re-runs of saved (or inline) questions. Webhook
 * delivery posts a JSON payload with the result rows + narrative. The
 * "Run now" button fires the schedule once for testing.
 */
export function DigestsPage() {
  const qc = useQueryClient();
  const { data: schedules, isLoading, error, refetch } = useQuery<DigestSchedule[]>({
    queryKey: ["digests"],
    queryFn: listDigests,
  });
  const { data: saved } = useQuery<SavedQuestion[]>({
    queryKey: ["saved-questions"],
    queryFn: listSavedQuestions,
  });
  const { data: runs } = useQuery<DigestRun[]>({
    queryKey: ["digest-runs", "recent"],
    queryFn: listRecentDigestRuns,
    refetchInterval: 30_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteDigest(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["digests"] }),
  });
  const runNow = useMutation({
    mutationFn: (id: string) => runDigestNow(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["digests"] });
      qc.invalidateQueries({ queryKey: ["digest-runs", "recent"] });
    },
  });

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-datumcraft-navy">
            Scheduled digests
          </h1>
          <p className="mt-0.5 text-sm text-datumcraft-slate">
            Re-run saved questions on a cron schedule and ship the result to a webhook.
            Cron expressions follow Spring's syntax: <code className="font-mono">sec min hour day month dow</code>.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-3 py-1 text-xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
        >
          <RefreshCcw size={11} />
          Refresh
        </button>
      </header>

      {isLoading && <p className="text-sm text-datumcraft-slate">Loading…</p>}
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          Failed to load — {(error as Error).message}
        </p>
      )}

      <div data-tour-id="digests-create">
        <CreateDigestForm
          savedQuestions={saved ?? []}
          onCreated={() => qc.invalidateQueries({ queryKey: ["digests"] })}
        />
      </div>

      {schedules && schedules.length > 0 && (
        <ul data-tour-id="digests-list" className="mt-6 grid gap-2">
          {schedules.map((s) => (
            <ScheduleRow
              key={s.id}
              schedule={s}
              savedQuestion={saved?.find((q) => q.id === s.savedQuestionId) ?? null}
              onRunNow={() => runNow.mutate(s.id)}
              onDelete={() => {
                if (confirm(`Delete digest "${s.name}"?`)) remove.mutate(s.id);
              }}
              isRunning={runNow.isPending && runNow.variables === s.id}
            />
          ))}
        </ul>
      )}

      {runs && runs.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-base font-semibold text-datumcraft-navy">
            Recent runs
          </h2>
          <ul className="mt-2 grid gap-1.5">
            {runs.slice(0, 30).map((r) => (
              <RunRow key={r.id} run={r} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ScheduleRow({
  schedule,
  savedQuestion,
  onRunNow,
  onDelete,
  isRunning,
}: {
  schedule: DigestSchedule;
  savedQuestion: SavedQuestion | null;
  onRunNow: () => void;
  onDelete: () => void;
  isRunning: boolean;
}) {
  const tone =
    schedule.lastStatus === "FAILED"
      ? "border-rose-200 bg-rose-50/40"
      : schedule.lastStatus === "OK"
      ? "border-emerald-200 bg-emerald-50/40"
      : "border-datumcraft-slate/20 bg-white";
  return (
    <li className={`flex flex-col gap-2 rounded-md border p-3 ${tone}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <CalendarClock size={14} className="text-datumcraft-steel" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-datumcraft-ink">{schedule.name}</span>
              {!schedule.active && (
                <span className="rounded-full border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-slate">
                  inactive
                </span>
              )}
            </div>
            <p className="text-2xs font-mono text-datumcraft-slate">
              {schedule.cronExpression} ·{" "}
              <span className="text-datumcraft-steel">{schedule.deliveryAction.toLowerCase()}</span>
              {schedule.deliveryTarget && <> → {schedule.deliveryTarget}</>}
            </p>
            <p className="mt-1 text-2xs text-datumcraft-slate">
              {savedQuestion
                ? <>Question: <span className="font-medium text-datumcraft-ink">{savedQuestion.name}</span></>
                : schedule.inlineQuestion
                ? <>Inline: <span className="italic">{truncate(schedule.inlineQuestion, 80)}</span></>
                : "No question configured"}
            </p>
            {schedule.lastRunAt && (
              <p className="text-2xs text-datumcraft-slate">
                Last run {new Date(schedule.lastRunAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onRunNow}
            disabled={isRunning}
            className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-2xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist disabled:opacity-60"
          >
            <PlayCircle size={11} />
            {isRunning ? "Running…" : "Run now"}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-2xs font-medium text-rose-700 hover:bg-rose-50"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </li>
  );
}

function RunRow({ run }: { run: DigestRun }) {
  const icon =
    run.status === "SUCCEEDED" ? <CheckCircle2 size={11} className="text-emerald-600" />
    : run.status === "FAILED" ? <XCircle size={11} className="text-rose-600" />
    : <PlayCircle size={11} className="text-datumcraft-slate" />;
  return (
    <li className="flex items-center gap-2 rounded-md border border-datumcraft-slate/15 bg-white px-3 py-1.5 text-xs">
      {icon}
      <span className="font-mono text-2xs text-datumcraft-slate">
        {new Date(run.startedAt).toLocaleString()}
      </span>
      <span className="text-datumcraft-ink">
        {run.rowCount != null && `${run.rowCount} rows · `}
        {run.deliveryStatus?.toLowerCase() ?? run.status.toLowerCase()}
      </span>
      {run.deliveryDetail && (
        <span className="ml-auto truncate text-2xs text-datumcraft-slate" title={run.deliveryDetail}>
          {truncate(run.deliveryDetail, 60)}
        </span>
      )}
    </li>
  );
}

function CreateDigestForm({
  savedQuestions,
  onCreated,
}: {
  savedQuestions: SavedQuestion[];
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [cron, setCron] = useState("0 0 9 * * MON-FRI");
  const [savedQuestionId, setSavedQuestionId] = useState("");
  const [inlineQuestion, setInlineQuestion] = useState("");
  const [deliveryAction, setDeliveryAction] = useState<DigestSchedule["deliveryAction"]>("WEBHOOK");
  const [deliveryTarget, setDeliveryTarget] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      createDigest({
        name: name.trim(),
        cronExpression: cron.trim(),
        savedQuestionId: savedQuestionId || null,
        inlineQuestion: savedQuestionId ? null : (inlineQuestion.trim() || null),
        deliveryAction,
        deliveryTarget: deliveryTarget.trim() || null,
        active: true,
      }),
    onSuccess: () => {
      setName("");
      setInlineQuestion("");
      setSavedQuestionId("");
      setDeliveryTarget("");
      setError(null);
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <section className="rounded-xl border border-datumcraft-slate/20 bg-white p-4">
      <h2 className="font-display text-base font-semibold text-datumcraft-navy">
        New digest
      </h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Friday morning audit"
            className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Cron</span>
          <input
            value={cron}
            onChange={(e) => setCron(e.target.value)}
            className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 font-mono text-sm"
          />
        </label>
      </div>

      <label className="mt-3 block text-xs">
        <span className="mb-1 block font-medium text-datumcraft-ink">Saved question (optional)</span>
        <select
          value={savedQuestionId}
          onChange={(e) => setSavedQuestionId(e.target.value)}
          className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1.5 text-sm"
        >
          <option value="">— Use inline question below —</option>
          {savedQuestions.map((q) => (
            <option key={q.id} value={q.id}>{q.name}</option>
          ))}
        </select>
      </label>

      {!savedQuestionId && (
        <label className="mt-3 block text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Inline question</span>
          <textarea
            value={inlineQuestion}
            onChange={(e) => setInlineQuestion(e.target.value)}
            rows={3}
            placeholder="e.g. Show me the top 10 reports that failed in the last 24 hours."
            className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 text-sm"
          />
        </label>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Delivery</span>
          <select
            value={deliveryAction}
            onChange={(e) => setDeliveryAction(e.target.value as DigestSchedule["deliveryAction"])}
            className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1.5 text-sm"
          >
            <option value="LOG">Log only</option>
            <option value="WEBHOOK">Webhook</option>
            <option value="EMAIL">Email (not yet wired)</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Target</span>
          <input
            value={deliveryTarget}
            onChange={(e) => setDeliveryTarget(e.target.value)}
            placeholder={deliveryAction === "WEBHOOK" ? "https://hooks.example.com/…" : "ops@example.com"}
            disabled={deliveryAction === "LOG"}
            className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 text-sm disabled:bg-datumcraft-mist"
          />
        </label>
      </div>

      {error && (
        <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
          {error}
        </p>
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={() => submit.mutate()}
          disabled={!name.trim() || !cron.trim() || submit.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-datumcraft-ink disabled:opacity-50"
        >
          <Plus size={12} />
          {submit.isPending ? "Creating…" : "Create digest"}
        </button>
      </div>
    </section>
  );
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max) + "…";
}
